import { useCallback, useEffect, useRef, useState } from "react";
import { newIdemKey, idemHeaders } from "@/lib/idem";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { money, fmtDate, statusColor } from "@/lib/format";
import { waNumberFor, waBusinessMessage } from "@/lib/wa";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import { effectiveCreditMode, creditModeLabel, storedModeForEdit, accountStatusView } from "@/lib/creditMode";
import { toast } from "sonner";
import ManagerPinModal from "@/components/ManagerPinModal";
import { PrepaidPanel } from "@/components/prepaid/PrepaidPanel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { printDoc, itemsTable, totalsBlock, pickupBlock, pmoney } from "@/lib/print";
import DocumentShareDialog from "@/components/documents/DocumentShareDialog";
import { usePrintFormat } from "@/components/print/PrintFormatDialog";
import { isThermalFormat } from "@/lib/printFormat";
import { ArrowLeft, Phone, Mail, MapPin, Building2, CreditCard, Ban, AlertTriangle, Printer, FileText, Pencil, MessageCircle, Share2 } from "lucide-react";
import { CustomerForm } from "@/components/customers/CustomerForm";
import { CustomerLifecycle } from "@/components/customers/CustomerLifecycle";

export default function CustomerProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, business } = useAuth();
  const modules = useModules();
  const [c, setC] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "Cash", strategy: "oldest", invoice_id: "", note: "" });
  const payInFlight = useRef(false); const payIdemKey = useRef(null); const [payBusy, setPayBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [shareDoc, setShareDoc] = useState(null); // {kind:"payment"|"statement", id, label, email, phone, waMessage} or null
  // Presentation selector (Full Page default / Receipt 80·58) — same chooser the Quotation and Shift Report use.
  const { requestPrint: requestPaymentPrint, dialog: paymentPrintDialog } = usePrintFormat("payment_receipt", "Print Payment Receipt");
  const [woInv, setWoInv] = useState(null);
  const [woReason, setWoReason] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtRange, setStmtRange] = useState({ start: "", end: "" });
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState({ text: "", category: "General" });
  const [editOpen, setEditOpen] = useState(false); const [editForm, setEditForm] = useState(null); const [editPinOpen, setEditPinOpen] = useState(false); const [editPinMsg, setEditPinMsg] = useState(""); const [editPinTitle, setEditPinTitle] = useState("Credit Change Approval");
  const [editSaving, setEditSaving] = useState(false);
  const openEdit = () => { setEditForm({ ...c, credit_mode: storedModeForEdit(c, business), credit_terms_days: c.credit_terms_days || 30 }); setEditOpen(true); };
  const saveEdit = async (pin = null) => {
    if (editSaving) return;
    const keys = ["name", "company", "phone", "whatsapp_number", "email", "address", "trn", "account_status", "notes", "wallet_enabled", "credit_enabled", "credit_mode", "credit_limit", "credit_terms_days", "approval_id", "phone_region"];
    const body = Object.fromEntries(keys.filter((k) => editForm[k] !== undefined && editForm[k] !== null).map((k) => [k, editForm[k]]));
    if (!body.name?.trim()) { toast.error("Customer name is required"); return; }
    setEditSaving(true);
    try { await api.put(`/customers/${id}`, { ...body, manager_pin: pin }); toast.success("Customer updated"); setEditOpen(false); setEditPinOpen(false); load(); }
    catch (e) { const d = e.response?.data?.detail; if (d?.needs_approval) { setEditPinMsg(d.message); if (d.reason !== "approval_failed" && d.reason !== "approval_cooldown") setEditPinTitle(d.title || (d.owner_pin_required ? "Owner Approval Required" : "Credit Change Approval")); setEditPinOpen(true); } else toast.error(apiError(d)); }
    finally { setEditSaving(false); }
  };

  const loadNotes = useCallback(() => api.get(`/customers/${id}/notes`).then((r) => setNotes(r.data)).catch(() => {}), [id]);
  const entitlementOn = modules.isEnabled("customer_email");
  const emailOn = entitlementOn && !!business?.email_enabled;
  const canSensitive = user?.role !== "cashier";
  const addNote = async () => {
    if (!newNote.text.trim()) return;
    try { await api.post(`/customers/${id}/notes`, newNote); setNewNote({ text: "", category: "General" }); loadNotes(); toast.success("Note added"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const delNote = async (nid) => {
    if (!window.confirm("Delete this note?")) return;
    try { await api.delete(`/customers/${id}/notes/${nid}`); loadNotes(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const downloadPdf = () => {
    const p = new URLSearchParams(stmtRange).toString();
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/customers/${id}/statement.pdf?${p}`, "_blank");
  };

  const load = useCallback(() => api.get(`/customers/${id}`).then((r) => setC(r.data)), [id]);
  useEffect(() => { load(); loadNotes(); }, [load, loadNotes]);

  if (!c) return <p className="text-muted-foreground">Loading…</p>;

  const overdue = c.max_days_overdue > 0;
  const cur = business?.currency || "JMD $";

  const printStatement = async () => {    const { data } = await api.get(`/customers/${id}/statement`, { params: stmtRange });
    const rows = data.lines.map((l) => `<tr><td>${l.date}</td><td>${l.type}</td><td>${l.ref || ""}</td><td class="right">${l.debit ? pmoney(l.debit, cur) : ""}</td><td class="right">${l.credit ? pmoney(l.credit, cur) : ""}</td><td class="right">${pmoney(l.balance, cur)}</td></tr>`).join("");
    const body = `<div class="section"><h3>Opening Balance: ${pmoney(data.opening_balance, cur)}</h3>
      <table class="data"><thead><tr><th>Date</th><th>Type</th><th>Reference</th><th class="right">Charges</th><th class="right">Payments</th><th class="right">Balance</th></tr></thead><tbody>${rows || "<tr><td colspan=6>No activity in range</td></tr>"}</tbody></table></div>
      <table class="totals">
        <tr><td>Total Invoiced</td><td class="right">${pmoney(data.total_invoiced, cur)}</td></tr>
        <tr><td>Total Paid</td><td class="right">${pmoney(data.total_paid, cur)}</td></tr>
        <tr><td>Overdue Amount</td><td class="right">${pmoney(data.overdue_amount, cur)}</td></tr>
        <tr class="grand"><td>Current Balance</td><td class="right">${pmoney(data.current_balance, cur)}</td></tr>
      </table>`;
    printDoc({ business, docTitle: "Customer Statement", docRef: c.name,
      meta: [["Account", c.company || c.name], ["Period", `${data.start} \u2192 ${data.end}`], ["Aging", data.aging_status]], body, accent: "#8b5cf6", showBank: true });
    setStmtOpen(false);
  };

  const emailStatement = async () => {
    try {
      const { data } = await api.post(`/customers/${id}/statement/email`, stmtRange);
      toast.success(`Statement queued to ${data.to}`);
      setStmtOpen(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const openSharePayment = (p) => {
    setShareDoc({
      kind: "payment", id: p.id, customerId: id, label: `Payment Receipt ${p.receipt_number}`, email: c.email, phone: waNumberFor(c),
      waMessage: waBusinessMessage({
        business, kind: "payment", heading: "Payment Receipt",
        meta: [`Reference: ${p.receipt_number}`, `Date: ${fmtDate(p.created_at)}`],
        greeting: c.name,
        body: [`Amount Received: *${money(p.amount)}*`, `Method: ${p.method}`, `Status: *Completed*`],
        closing: "Thank you.",
      }),
    });
  };

  const openShareStatement = () => {
    const bal = c.outstanding_balance || 0;
    setShareDoc({
      kind: "statement", id, customerId: id, label: `Customer Account Statement (A/R) · ${c.name}`, email: c.email, phone: waNumberFor(c),
      waMessage: waBusinessMessage({
        business, kind: "statement", heading: "Account Statement",
        meta: [`As of ${fmtDate(new Date().toISOString())}`],
        greeting: c.name,
        body: [`Outstanding Balance: *${money(bal)}*`],
        closing: bal > 0 ? "We appreciate your prompt attention to this balance." : "Your account is fully up to date. Thank you.",
      }),
    });
  };

  // One stored payment record `p` → two presentations. Presentation only: no API call, nothing written.
  const printPaymentReceipt = (p, format = "standard") => {
    const allocRows = (p.allocations || []);
    const hasSnapshot = p.balance_after != null;   // legacy payments without a stored snapshot omit the line
    if (!isThermalFormat(format)) {
      const body = `<table class="data"><tbody>
      <tr><td>Amount Received</td><td class="right"><strong>${pmoney(p.amount, cur)}</strong></td></tr>
      <tr><td>Method</td><td class="right">${p.method}</td></tr>
      ${allocRows.map((a) => `<tr><td>Applied to ${a.invoice_number}</td><td class="right">${pmoney(a.amount, cur)}</td></tr>`).join("")}
      ${hasSnapshot ? `<tr><td>Account Balance</td><td class="right">${pmoney(p.balance_after, cur)}</td></tr>` : ""}
    </tbody></table>`;
      printDoc({ business, docTitle: "Payment Receipt", docRef: p.receipt_number, inline: true,   // Full Page → same-tab frame + system dialog (global rule)
        meta: [["Customer", c.name], ["Date", fmtDate(p.created_at)]], body });
      return;
    }
    // Thermal: existing shared `plain` record path (as Shift Report) — keeps store name + title, drops contact lines / policy / footer.
    const row = (l, v) => `<div class="row"><span class="lbl">${l}</span><span class="val">${v}</span></div>`;
    const body = `<style>.row .lbl{white-space:normal!important}.row .val{white-space:nowrap}</style>
      <div data-doc="payment-rows">${row("Method", p.method)}${allocRows.map((a) => row(`Applied to ${a.invoice_number}`, pmoney(a.amount, cur))).join("")}${p.unapplied > 0 ? row("Unapplied credit", pmoney(p.unapplied, cur)) : ""}</div>
      <table class="totals" data-doc="payment-totals">
        <tr class="grand"><td>Amount Paid</td><td class="right num">${pmoney(p.amount, cur)}</td></tr>
        ${hasSnapshot ? `<tr class="cash"><td>Account Balance</td><td class="right num">${pmoney(p.balance_after, cur)}</td></tr>` : ""}
      </table>`;
    printDoc({ business, docTitle: "Payment Receipt", docRef: p.receipt_number, format, plain: true,
      meta: [["Customer", c.name], ["Date", new Date(p.created_at).toLocaleString()], ["Received by", p.created_by || ""]], body });
  };

  const printInvoice = async (row) => {
    let inv = row;
    if (row.pickup_reference) {
      try { inv = (await api.get(`/sales/${row.id}`)).data; } catch { /* fall back to the row */ }
    }
    const body = itemsTable(inv.items, cur, { showFulfillment: !!inv.pickup_reference })
      + totalsBlock({ subtotal: inv.subtotal, tax: inv.tax, total: inv.total, taxLabel: business?.tax_label || "Tax", paid: inv.amount_paid, balance: inv.remaining_balance, deliveryFee: inv.delivery_enabled ? inv.delivery_fee : 0, deliveryTax: inv.delivery_enabled ? inv.delivery_tax : 0 }, cur)
      + pickupBlock(inv);
    printDoc({ business, docTitle: inv.is_credit ? "Invoice" : "Receipt", docRef: inv.invoice_number,
      meta: [["Customer", c.name], ["Date", fmtDate(inv.sale_date)], ...(inv.is_credit ? [["Due Date", fmtDate(inv.due_date)]] : [])], body, showBank: !!inv.is_credit, receipt: true });
  };

  const recordPayment = async () => {
    if (payInFlight.current) return;
    payInFlight.current = true; setPayBusy(true);
    try {
      if (!payIdemKey.current) payIdemKey.current = newIdemKey();
      const { data } = await api.post("/payments", { ...pay, customer_id: id, amount: parseFloat(pay.amount) }, idemHeaders(payIdemKey.current));
      payIdemKey.current = null;
      setReceipt(data.receipt); setPayOpen(false); setPay({ amount: "", method: "Cash", strategy: "oldest", invoice_id: "", note: "" });
      toast.success(`Payment recorded · ${data.receipt.receipt_number}`); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { payInFlight.current = false; setPayBusy(false); }
  };

  const doWriteOff = async (pin) => {
    try {
      await api.post(`/invoices/${woInv.id}/write-off`, { manager_pin: pin, reason: woReason });
      toast.success("Balance written off"); setPinOpen(false); setWoInv(null); setWoReason(""); load();
    } catch (e) { throw new Error("Invalid PIN"); }
  };

  return (
    <div className="space-y-6">
      {paymentPrintDialog}
      <button onClick={() => nav("/customers")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to Customers
      </button>

      <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
        {/* Profile */}
        <div className="space-y-4">
          <Card className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center font-head font-extrabold text-2xl">{c.name[0]}</div>
              <div>
                <h1 className="font-head text-xl font-extrabold">{c.name}</h1>
                {overdue ? <Badge className="bg-rose-500/15 text-rose-500 border-rose-500/30"><AlertTriangle className="w-3 h-3 mr-1" />{c.max_days_overdue}d overdue</Badge>
                  : <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 capitalize">{c.account_status}</Badge>}
              </div>
            </div>
            <div className="mt-5 space-y-2 text-sm" data-testid="customer-details-section">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Customer Details</p>
              {c.company && <Line icon={Building2} v={c.company} />}
              {c.phone && <Line icon={Phone} v={c.phone} />}
              {c.whatsapp_number && c.whatsapp_number !== c.phone && <Line icon={MessageCircle} v={`WhatsApp: ${c.whatsapp_number}`} />}
              {c.email && <Line icon={Mail} v={c.email} />}
              {c.address && <Line icon={MapPin} v={c.address} />}
              {c.trn && <Line icon={CreditCard} v={`TRN: ${c.trn}`} />}
            </div>
            <Button onClick={() => openEdit()} variant="outline" className="w-full mt-5" data-testid="edit-customer-btn">
              <Pencil className="w-4 h-4 mr-2" /> Edit Customer & Account Features
            </Button>
            {(c.credit_enabled || c.outstanding_balance > 0) && <Button onClick={() => setPayOpen(true)} className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="record-payment-btn">
              <CreditCard className="w-4 h-4 mr-2" /> Record Payment
            </Button>}
            {(c.credit_enabled || c.outstanding_balance > 0 || (c.transaction_history || []).some((i) => i.is_credit)) && <Button onClick={() => setStmtOpen(true)} variant="outline" className="w-full mt-2" data-testid="statement-btn">
              <FileText className="w-4 h-4 mr-2" /> Customer Account Statement <span className="text-muted-foreground ml-1 text-xs">(A/R)</span>
            </Button>}
          </Card>

          <CustomerLifecycle customer={c} user={user} onChanged={load} onDeleted={() => nav("/customers")} />

          <Card className="p-5" data-testid="notes-timeline">
            <p className="font-head font-bold text-sm mb-3">Notes</p>
            <div className="space-y-2">
              <textarea value={newNote.text} onChange={(e) => setNewNote({ ...newNote, text: e.target.value })}
                placeholder="Add a note…" className="w-full text-sm rounded-lg border border-input bg-background p-2 min-h-[60px]" data-testid="note-text" />
              <div className="flex gap-2">
                <select value={newNote.category} onChange={(e) => setNewNote({ ...newNote, category: e.target.value })}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm flex-1" data-testid="note-category">
                  <option>General</option><option>Payment Follow-up</option>
                  {canSensitive && <><option>Credit</option><option>Collection</option></>}
                </select>
                <Button size="sm" onClick={addNote} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-note-btn">Add</Button>
              </div>
            </div>
            <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
              {notes.length === 0 ? <p className="text-xs text-muted-foreground">No notes yet.</p> :
                notes.map((n) => (
                  <div key={n.id} className="text-sm border-l-2 border-border pl-3" data-testid={`note-${n.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{n.category}</Badge>
                      {n.legacy && <Badge variant="outline" className="text-[10px] text-amber-600">Legacy</Badge>}
                      <span className="text-[11px] text-muted-foreground">{(n.created_at || "").slice(0, 16).replace("T", " ")} · {n.created_by}{n.edited_at ? " · edited" : ""}</span>
                      {canSensitive && <button onClick={() => delNote(n.id)} className="ml-auto text-rose-500 text-xs hover:underline" data-testid={`note-del-${n.id}`}>Delete</button>}
                    </div>
                    <p className="mt-0.5 text-muted-foreground">{n.text}</p>
                  </div>
                ))}
            </div>
          </Card>

          {(c.credit_enabled || c.outstanding_balance > 0) ? (
          <div data-testid="customer-credit-section">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Customer Credit / A/R <span className="normal-case font-normal">· amount owed to you{!c.credit_enabled ? " · credit not enabled (historical balance)" : ""}</span></p>
            {!modules.isEnabled("customers_credit") && <p className="text-xs text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2 mb-2" data-testid="credit-module-off-note">Customer Credit is disabled for new activity. Existing balances remain available for collection.</p>}
            <div className="grid grid-cols-2 gap-3">
              {c.credit_enabled && <MiniStat label="Credit Type" value={creditModeLabel(c)} testid="customer-credit-type" />}
              {effectiveCreditMode(c) === "credit_limit" && <MiniStat label="Credit Limit" value={money(c.credit_limit)} />}
              <MiniStat label="Amount Owed" value={money(c.outstanding_balance)} accent={c.outstanding_balance > 0 ? "text-amber-600" : ""} testid="customer-amount-owed" />
              {effectiveCreditMode(c) === "credit_limit" && <MiniStat label="Available Credit" value={money(c.available_credit ?? 0)} accent={(c.available_credit ?? 0) < 0 ? "text-rose-500" : "text-emerald-500"} />}
              {c.credit_enabled && <MiniStat label="Payment Terms" value={`${c.credit_terms_days} days`} testid="customer-payment-terms" />}
              {effectiveCreditMode(c) === "open_account" && (() => { const st = accountStatusView(c); return <MiniStat label="Account Status" value={st.label} accent={st.accent} testid="customer-account-status" />; })()}
            </div>
          </div>
          ) : (
            <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border p-3" data-testid="customer-credit-off">{modules.isEnabled("customers_credit") ? "" : "Customer Credit is not enabled for this business. "}Customer Credit is not enabled — this is a regular{c.wallet_enabled && modules.isEnabled("prepaid_accounts") ? " / Wallet" : ""} customer. Purchases are still recorded to this customer. Enable credit under <b>Edit Customer</b> when needed.</p>
          )}
        </div>

        {/* Wallet (feature-gated) + Purchase History */}
        <div className="space-y-6 min-w-0">
        <PrepaidPanel customerId={id} business={business} />
        <Card className="p-0 overflow-hidden" data-testid="purchase-history-section">
          <Tabs defaultValue="tx">
            <div className="p-4 border-b border-border">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Purchase History</p>
              <TabsList>
                <TabsTrigger value="tx" data-testid="tab-transactions">Invoices & Purchases</TabsTrigger>
                <TabsTrigger value="pay" data-testid="tab-payments">A/R Payments</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="tx" className="m-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead>Due</TableHead>
                  <TableHead className="text-right">Total</TableHead><TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {c.transaction_history.map((inv) => (
                    <TableRow key={inv.id} data-testid={`inv-row-${inv.id}`}>
                      <TableCell className="mono text-xs">{inv.invoice_number}{inv.source?.quote_number && <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-sky-500/15 text-sky-500 border border-sky-500/20" title="Created from quotation">← {inv.source.quote_number}</span>}</TableCell>
                      <TableCell className="text-xs">{fmtDate(inv.sale_date)}</TableCell>
                      <TableCell className="text-xs">{inv.is_credit ? fmtDate(inv.due_date) : "-"}<br />
                        {inv.is_credit && inv.remaining_balance > 0 && <span className="text-[10px] text-muted-foreground">{inv.days_outstanding}d outstanding</span>}</TableCell>
                      <TableCell className="text-right mono">{money(inv.total)}</TableCell>
                      <TableCell className="text-right mono font-bold">{money(inv.remaining_balance)}</TableCell>
                      <TableCell><Badge className={statusColor(inv.status)}>{inv.status}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => printInvoice(inv)} data-testid={`print-inv-${inv.id}`}><Printer className="w-3.5 h-3.5" /></Button>
                          {inv.is_credit && inv.remaining_balance > 0 && !inv.written_off && (
                            <Button size="sm" variant="ghost" className="text-rose-500 h-7 w-7 p-0" onClick={() => { setWoInv(inv); setPinOpen(true); }} data-testid={`writeoff-${inv.id}`}>
                              <Ban className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {c.transaction_history.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No transactions yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TabsContent>
            <TabsContent value="pay" className="m-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Receipt</TableHead><TableHead>Date</TableHead><TableHead>Method</TableHead>
                  <TableHead>Applied To</TableHead><TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {c.payment_history.map((p) => (
                    <TableRow key={p.id} data-testid={`pay-row-${p.id}`}>
                      <TableCell className="mono text-xs"><button className="hover:text-emerald-500 flex items-center gap-1" onClick={() => requestPaymentPrint((format) => printPaymentReceipt(p, format))} data-testid={`print-pay-${p.id}`}><Printer className="w-3 h-3" />{p.receipt_number}</button></TableCell>
                      <TableCell className="text-xs">{fmtDate(p.created_at)}</TableCell>
                      <TableCell>{p.method}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.allocations?.map((a) => a.invoice_number).join(", ") || "General"}</TableCell>
                      <TableCell className="text-right mono font-bold text-emerald-500">{money(p.amount)}</TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-600" title="Digital Share"
                          onClick={() => openSharePayment(p)} data-testid={`payment-share-${p.id}`}><Share2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {c.payment_history.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No payments yet.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TabsContent>
          </Tabs>
        </Card>
        </div>
      </div>

      {/* Payment modal */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-md" data-testid="payment-form">
          <DialogHeader><DialogTitle className="font-head">Record Payment · {c.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Amount</Label>
              <Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} className="mono text-lg h-12" data-testid="pay-amount" placeholder="0.00" />
              <p className="text-xs text-muted-foreground mt-1">Outstanding: {money(c.outstanding_balance)}</p>
            </div>
            <div>
              <Label className="text-xs">Payment Method</Label>
              <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="pay-method">
                <option>Cash</option><option>WiPay/Card</option><option>Bank Transfer</option><option>Cheque</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Apply To</Label>
              <select value={pay.strategy} onChange={(e) => setPay({ ...pay, strategy: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="pay-strategy">
                <option value="oldest">Oldest outstanding balance</option>
                <option value="invoice">Specific invoice</option>
                <option value="general">General account payment</option>
              </select>
            </div>
            {pay.strategy === "invoice" && (
              <div>
                <Label className="text-xs">Invoice</Label>
                <select value={pay.invoice_id} onChange={(e) => setPay({ ...pay, invoice_id: e.target.value })} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="pay-invoice">
                  <option value="">Select…</option>
                  {c.transaction_history.filter((i) => i.remaining_balance > 0).map((i) => <option key={i.id} value={i.id}>{i.invoice_number} · {money(i.remaining_balance)}</option>)}
                </select>
              </div>
            )}
            <Button onClick={recordPayment} disabled={payBusy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="submit-payment">{payBusy ? "Processing…" : "Record & Generate Receipt"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt */}
      <Dialog open={!!receipt} onOpenChange={() => setReceipt(null)}>
        <DialogContent className="max-w-sm" data-testid="payment-receipt">
          <DialogHeader><DialogTitle className="font-head text-center">Payment Receipt</DialogTitle></DialogHeader>
          <div className="text-center mono text-sm text-muted-foreground">{receipt?.receipt_number}</div>
          <div className="border-y border-dashed border-border py-3 space-y-1.5">
            <div className="flex justify-between"><span className="text-muted-foreground text-sm">Amount</span><span className="mono font-bold text-lg text-emerald-500">{money(receipt?.amount)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Method</span><span>{receipt?.method}</span></div>
            {receipt?.allocations?.map((a) => <div key={a.invoice_id} className="flex justify-between text-xs"><span className="text-muted-foreground">{a.invoice_number}</span><span className="mono">{money(a.amount)}</span></div>)}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => requestPaymentPrint((format) => printPaymentReceipt(receipt, format))} data-testid="print-payment-receipt"><Printer className="w-4 h-4 mr-2" /> Print / PDF</Button>
            <Button variant="outline" onClick={() => openSharePayment(receipt)} data-testid="share-payment-receipt"><Share2 className="w-4 h-4 mr-2" /> Digital Share</Button>
          </div>
          <Button className="w-full mt-2" onClick={() => setReceipt(null)} data-testid="done-payment-receipt">Done</Button>
        </DialogContent>
      </Dialog>

      {/* Statement */}
      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="max-w-md" data-testid="statement-modal">
          <DialogHeader><DialogTitle className="font-head">Customer Account Statement <span className="text-muted-foreground font-normal text-sm">(A/R)</span> · {c.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Generate a professional statement of account. Leave dates blank for all activity.</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div><Label className="text-xs">From</Label><DatePicker value={stmtRange.start} onChange={(v) => setStmtRange({ ...stmtRange, start: v })} data-testid="stmt-start" /></div>
            <div><Label className="text-xs">To</Label><DatePicker value={stmtRange.end} onChange={(v) => setStmtRange({ ...stmtRange, end: v })} data-testid="stmt-end" /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={printStatement} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="generate-statement"><FileText className="w-4 h-4 mr-2" /> Print</Button>
            <Button onClick={downloadPdf} variant="outline" data-testid="download-statement-pdf"><FileText className="w-4 h-4 mr-2" /> PDF</Button>
            <Button onClick={emailStatement} variant="outline" disabled={!emailOn} data-testid="email-statement"><Mail className="w-4 h-4 mr-2" /> Email</Button>
            <Button onClick={openShareStatement} variant="outline" data-testid="whatsapp-statement"><Share2 className="w-4 h-4 mr-2" /> Digital Share</Button>
          </div>
          <p className="text-[11px] text-muted-foreground text-center" data-testid="email-statement-help">
            {emailOn ? "Email attaches the branded PDF and is logged."
              : !entitlementOn ? "Live Customer Email Delivery isn't activated for your account yet. Contact NEEC support to turn it on. PDF is always available."
              : "Email / Messaging is turned off. Enable it in Settings → Receipts & Documents. PDF is always available."}
          </p>
        </DialogContent>
      </Dialog>

      <ManagerPinModal open={pinOpen} onClose={() => setPinOpen(false)} title="Owner Approval Required"
        reason={woInv ? `Deleting (writing off) the remaining ${money(woInv.remaining_balance)} on ${woInv.invoice_number} is a protected action. Enter the Owner Authorization PIN.` : ""}
        onSubmit={doWriteOff} />
      <ManagerPinModal open={editPinOpen} onClose={() => setEditPinOpen(false)} title={editPinTitle} reason={editPinMsg} onSubmit={(pin) => saveEdit(pin)} />
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="customer-edit-form">
          <DialogHeader><DialogTitle className="font-head">Edit Customer · {c.name}</DialogTitle></DialogHeader>
          {editForm && <CustomerForm form={editForm} setForm={setEditForm} business={business} mode="edit" />}
          <Button onClick={() => saveEdit()} disabled={editSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-customer-edit">{editSaving ? "Saving…" : "Save Changes"}</Button>
        </DialogContent>
      </Dialog>

      <DocumentShareDialog open={!!shareDoc} onOpenChange={(o) => !o && setShareDoc(null)}
        title={shareDoc?.label || "Share Document"} docLabel={shareDoc?.label} waMessage={shareDoc?.waMessage}
        customerId={shareDoc?.customerId} customerEmail={shareDoc?.email} customerPhone={shareDoc?.phone}
        onCustomerUpdated={() => load()}
        fileName={`${shareDoc?.label || "document"}.pdf`}
        getPdfBlob={() => shareDoc?.kind === "statement"
          ? api.get(`/customers/${shareDoc.id}/statement.pdf`, { params: stmtRange, responseType: "blob" }).then((r) => r.data)
          : api.get(`/payments/${shareDoc.id}/receipt.pdf`, { responseType: "blob" }).then((r) => r.data)}
        onEmail={(idem) => shareDoc?.kind === "statement"
          ? api.post(`/customers/${shareDoc.id}/statement/email`, { ...stmtRange, idempotency_key: idem })
          : api.post(`/payments/${shareDoc.id}/receipt/email`, { idempotency_key: idem })}
        onWhatsAppShared={(method) => shareDoc?.kind === "statement"
          ? api.post(`/customers/${shareDoc.id}/statement/whatsapp-shared`, { method }).catch(() => {})
          : api.post(`/payments/${shareDoc.id}/receipt/whatsapp-shared`, { method }).catch(() => {})} />
    </div>
  );
}

const Line = ({ icon: Icon, v }) => <div className="flex items-center gap-2 text-muted-foreground"><Icon className="w-4 h-4 shrink-0" /><span className="text-foreground">{v}</span></div>;
const MiniStat = ({ label, value, accent, testid }) => (
  <Card className="p-3.5" data-testid={testid}><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p><p className={`mono font-bold text-lg ${accent || ""}`}>{value}</p></Card>
);
